// src/shared/kontoExport.js — общият диалог „Експорт за Конто" (ПРОМТ-11) и
// списъкът „Експорти".
//
// Защо един диалог за четирите файла (продажби, доставки, артикули,
// изписвания): човекът вижда всеки път едно и също — първо ПРЕГЛЕД („N
// документа OK / M с грешки"), после един бутон „Свали файла за Конто", който
// е неактивен, докато има грешка. Самият файл се строи и пази на сървъра
// (services/exportRuns.js); тук е само екранът.

import { el, clear } from './dom.js';
import { plural, periodLabel } from './format.js';
import { toast } from './toast.js';
import { api } from './api.js';
import { openModal, closeModal } from './modal.js';
import { state, cq, activeCompanyObj } from './state.js';
import { kontoSettingsDialog } from './kontoSettings.js';

export const KIND_LABEL = { sales: 'Продажби', purchases: 'Доставки', articles: 'Артикули', cogs: 'Изписвания', partners: 'Нови клиенти' };
// Къде в Конто 6 се внася всеки файл — казва се в диалога и в списъка.
export const WHERE = {
  sales: 'В Конто: Администриране → Връзка с други системи → Импорт на документи.',
  // ПРОМТ-29: и доставките са записвания (за входящи фактури „Импорт на
  // документи" няма шаблон — ОТГОВОР-ПРОМТ-27, т. 2.8).
  purchases: 'В Конто: Администриране → Връзка с други системи → Импорт на записвания.',
  articles: 'В Конто: Номенклатури → Импорт на номенклатури от Excel.',
  cogs: 'В Конто: Администриране → Връзка с други системи → Импорт на записвания.',
  // Етап 3: продажбите като записвания и новите клиенти към тях.
  records: 'В Конто: Администриране → Връзка с други системи → Импорт на записвания.',
  partners: 'В Конто: Счетоводство → Номенклатури → Импорт на номенклатури.',
};

function alertBox(color, text) {
  return el('div', { class: 'alert ' + color, role: color === 'red' ? 'alert' : 'status', style: 'margin-top:8px' }, el('div', { text }));
}

export async function downloadRun(runId, filename) {
  const res = await fetch('/api/export-runs/' + runId + '/file', { credentials: 'same-origin' });
  if (!res.ok) { toast('Файлът не можа да се свали.', true); return false; }
  const blob = await res.blob();
  const a = el('a', { href: URL.createObjectURL(blob), download: filename || 'konto6' });
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}

// Грешките идват като { number, text } (документи) или като низове (склад).
function errText(e) { return typeof e === 'string' ? e : ((e.number ? '№ ' + e.number + ': ' : '') + e.text); }

// opts: { kind, title, intro, url, payload() → тяло, controls: [елементи],
//   headline(r) → текст, onApplied(r) → по избор, вместо свалянето от
//   сървъра (продажби на summary), onDone() }.
// Контролите (месец, „всички") викат dialog.reload() при промяна.
export function kontoExportDialog(opts) {
  if (!state().activeCompany) { toast('Първо изберете фирма.', true); return null; }
  const box = el('div', { 'aria-live': 'polite' });
  const dlBtn = el('button', { class: 'btn', text: 'Свали файла за Конто', disabled: true, onClick: doExport });
  let last = null;

  const body = () => Object.assign({ company_id: state().activeCompany }, opts.payload ? opts.payload() : {});

  async function reload() {
    clear(box);
    box.appendChild(el('p', { class: 'muted', text: 'Проверявам…' }));
    dlBtn.disabled = true;
    let r;
    try { r = await api(opts.url, { method: 'POST', body: JSON.stringify(Object.assign(body(), { mode: 'preview' })) }); } catch (e) { clear(box); box.appendChild(alertBox('red', e.message)); return; }
    last = r;
    render(r);
  }

  function render(r) {
    clear(box);
    const errors = (r.errors || []).concat(r.stops || []);
    const okCount = r.ok != null ? r.ok : r.count;
    box.appendChild(el('div', { class: 'konto-headline', style: 'margin:10px 0;padding:12px;border:1px solid var(--border);border-radius:8px' },
      el('div', { style: 'font-size:20px;font-weight:600', text: opts.headline ? opts.headline(r) : (plural(okCount, 'документ OK', 'документа OK') + (errors.length ? ' / ' + plural(errors.length, 'грешка', 'грешки') : '')) }),
      opts.sub ? el('div', { class: 'muted', text: opts.sub(r) }) : null));
    if (errors.length) {
      box.appendChild(el('p', { style: 'margin:8px 0 4px;font-weight:600', text: 'Файлът не се прави, докато не се поправи:' }));
      const ul = el('ul', { class: 'konto-errors', style: 'margin:0;padding-left:20px' });
      errors.forEach((e) => ul.appendChild(el('li', { text: errText(e) })));
      box.appendChild(ul);
    }
    for (const w of r.warnings || []) box.appendChild(alertBox('amber', errText(w)));
    if (opts.extra) { const x = opts.extra(r); if (x) box.appendChild(x); }
    box.appendChild(el('p', { class: 'muted', style: 'margin-top:8px', text: (opts.where && opts.where(r)) || WHERE[opts.kind] }));
    const count = r.count != null ? r.count : (r.eligible != null ? r.eligible : okCount);
    dlBtn.disabled = !!errors.length || !count;
  }

  async function doExport() {
    dlBtn.disabled = true;
    try {
      const r = await api(opts.url, { method: 'POST', body: JSON.stringify(Object.assign(body(), { mode: 'apply' })) });
      // onApplied → true: файлът е направен от екрана (продажби на summary —
      // байт по байт досегашният файл от браузъра) и той сам казва „готово".
      if (!(opts.onApplied && opts.onApplied(r) === true)) {
        if (r.run_id) await downloadRun(r.run_id, r.filename || ('konto6-' + opts.kind + '.xlsx'));
        toast('Файлът за Конто е готов.');
      }
      closeModal();
      if (opts.onDone) opts.onDone(r);
    } catch (e) {
      toast(e.message, true);
      // Грешките от проверката идват с отговора — показват се в диалога.
      if (e.data && (e.data.errors || e.data.stops)) render(Object.assign({}, last || {}, e.data));
      else dlBtn.disabled = false;
    }
  }

  (opts.controls || []).forEach((c) => {
    const input = c.matches && c.matches('input,select') ? c : c.querySelector && c.querySelector('input,select');
    if (input) input.addEventListener('change', reload);
  });
  const content = el('div', {},
    opts.intro ? el('p', { class: 'muted', text: opts.intro }) : null,
    ...(opts.controls || []),
    box);
  // Продажби и доставки (Промени 16.10, т. 3г): грешката „задайте кода…"
  // се оправя от същия прозорец — без лутане из менютата; след запис
  // прегледът се прави наново.
  const settingsBtn = (opts.kind === 'sales' || opts.kind === 'purchases' || opts.kind === 'cogs')
    ? el('button', { class: 'btn ghost', text: 'Настройки за Конто…', style: 'margin-right:auto', onClick: () => kontoSettingsDialog(activeCompanyObj(), () => reload(), { kind: opts.kind }) })
    : null;
  const foot = el('div', {}, settingsBtn, el('button', { class: 'btn ghost', text: 'Затвори', onClick: closeModal }), dlBtn);
  openModal({ title: opts.title || 'Експорт за Конто', body: content, foot, wide: true });
  reload();
  return { reload, button: dlBtn, box };
}

// ---- Продажбите като записвания (Етап 3) ----

const fmt2 = (n) => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);
const lotText = (lot) => (lot && lot.length ? ' [' + lot.join('][') + ']' : '');

const CREDIT_LABEL = { none: ' · без право на кредит', partial: ' · частичен кредит' };

// Прегледът: как ще се осчетоводят първите документи — счетоводителят вижда
// статията, преди файлът да тръгне към Конто. (ПРОМТ-28: „новите клиенти" за
// Excel ги няма — Конто ги създава сам от PARTNER_* в същия файл.) Същият
// преглед и за доставките (ПРОМТ-29): client е доставчикът.
export function recordsPreview(r) {
  const wrap = el('div', {});
  if (r.items && r.items.length) {
    const det = el('details', { style: 'margin-top:8px' }, el('summary', { text: 'Как ще се осчетоводи (' + plural(r.items.length, 'документ', 'документа') + ')' }));
    const ul = el('ul', { class: 'records-preview', style: 'margin:4px 0 0;padding-left:20px' });
    r.items.slice(0, 20).forEach((it) => ul.appendChild(el('li', {},
      el('div', { style: 'font-weight:600', text: '№ ' + it.number + ' · ' + it.client + (it.cash ? ' · в брой' : '') + (CREDIT_LABEL[it.vat_credit] || '') }),
      el('div', { class: 'muted', text: it.lines.map((l) => l.side + ' ' + l.account + lotText(l.lot) + ' ' + fmt2(l.value) + (l.net != null ? ' (основа ' + fmt2(l.net) + ')' : '')).join(' · ') }))));
    det.appendChild(ul);
    if (r.items.length > 20) det.appendChild(el('div', { class: 'muted', text: '…и още ' + (r.items.length - 20) }));
    wrap.appendChild(det);
  }
  const ne = newElementsBox(r);
  if (ne) wrap.appendChild(ne);
  return wrap.childNodes.length ? wrap : null;
}

// ПРОМТ-31: елементите на номенклатури (статии, артикули), които ги няма в
// Конто на фирмата — Конто ги създава при вноса с кода и името от файла
// (колоните L_<ном.>_NAME), след като попита. Човекът трябва да знае какво ще
// се появи и какво да отговори.
export const NEW_ELEMENTS_TEXT = 'При „Запис" Конто ще попита „Намерени са несъществуващи елементи на номенклатури! Желаете ли да бъдат генерирани автоматично?" — отговорете „Да". Създава ги с точно тези кодове и имена.';
export function newElementsBox(r) {
  const list = Array.isArray(r && r.new_elements) ? r.new_elements : [];
  if (!list.length) return null;
  const box = el('div', { class: 'alert amber konto-new-elements', role: 'status', style: 'margin-top:8px' },
    el('div', { style: 'font-weight:600' }, el('span', { text: 'Нови елементи, които Конто ще създаде' }), ' (' + plural(list.length, 'елемент', 'елемента') + ')'),
    el('ul', { style: 'margin:4px 0;padding-left:20px' }, ...list.map((e) => el('li', { text: 'Номенклатура ' + e.nom + (e.nom_name ? ' „' + e.nom_name + '"' : '') + ': код ' + e.code + ' — ' + e.name }))),
    el('div', { class: 'muted', text: NEW_ELEMENTS_TEXT }));
  return box;
}

// След свалянето: ЕДИН файл (ПРОМТ-28) и какво да се провери в Конто.
// opts.kind: 'sales' (по подразбиране) или 'purchases' (ПРОМТ-29).
const RESULT_TEXT = {
  sales: {
    check: 'Формирайте дневника за продажби за периода и сверете оборотната ведомост на 411, 501, 70x и 4532 със сумите в CRM-а. Клиент, който Конто не познава, се създава сам с код по номерацията на Конто — при следващия „Внос от Конто" кодът влиза в CRM-а.',
    again: 'Не зареждайте един и същи файл два пъти — Конто не пречи на двоен внос. Ако трябва пак: Фактури → Действия → „Файлове за Конто" дава същия файл.',
  },
  purchases: {
    check: 'Формирайте дневника за покупки за месеца и сверете оборотната ведомост на 401, 4531 и разходните/складовите сметки със сумите в CRM-а. Доставчик, когото Конто не познава, се създава сам с код по номерацията на Конто — при следващия „Внос от Конто" кодът влиза в CRM-а.',
    again: 'Не зареждайте един и същи файл два пъти — Конто не пречи на двоен внос. Ако трябва пак: Склад → „Експорти за Конто" (или Фактури → Действия → „Файлове за Конто") дава същия файл.',
  },
};
export function recordsResultDialog(r, opts) {
  const T = RESULT_TEXT[(opts && opts.kind) || 'sales'] || RESULT_TEXT.sales;
  const step = (title, run, filename, where) => el('li', { style: 'margin-bottom:12px' },
    el('div', { style: 'font-weight:600', text: title }),
    run ? el('button', { class: 'btn sm', type: 'button', style: 'margin:6px 0', text: 'Свали ' + filename, onClick: () => downloadRun(run, filename) }) : null,
    el('div', { class: 'muted', text: where }));
  const ol = el('ol', { style: 'margin:0;padding-left:20px' });
  ol.appendChild(step('Статиите (' + plural(r.count, 'документ', 'документа') + ')', r.run_id, r.filename, WHERE.records + ' Избери → Зареди → Запис. Периодът трябва да е открит и незаключен.'));
  ol.appendChild(el('li', {},
    el('div', { style: 'font-weight:600', text: 'Проверка в Конто' }),
    el('div', { class: 'muted', text: T.check })));
  const body = el('div', {},
    el('p', { text: 'Заредете го в Конто:' }), ol,
    newElementsBox(r),
    el('p', { class: 'muted', text: T.again }));
  openModal({ title: 'Файлът за Конто е готов', body, foot: el('div', {}, el('button', { class: 'btn', type: 'button', text: 'Затвори', onClick: closeModal })) });
}

// Списъкът на свалените файлове — и за фирма без склад (там е в „Склад").
// opts.kind — само един вид (Доставки показват само своите файлове, ПРОМТ-34).
export function exportRunsDialog(opts) {
  const kind = opts && opts.kind;
  const host = el('div', {}, el('p', { class: 'muted', text: 'Зареждам…' }));
  openModal({ title: 'Файлове за Конто' + (kind && KIND_LABEL[kind] ? ' — ' + KIND_LABEL[kind].toLowerCase() : ''), body: host, foot: el('div', {}, el('button', { class: 'btn', type: 'button', text: 'Затвори', onClick: closeModal })), wide: true });
  renderExportRuns(host, { kind });
}

// Месец за избор: от текущия назад 12 месеца, по подразбиране — миналият
// (експортът е за приключен месец).
export function monthSelect(label) {
  const sel = el('select', { 'aria-label': label || 'Месец' });
  const d = new Date();
  for (let i = 0; i < 13; i++) {
    const y = d.getFullYear(), m = d.getMonth() + 1 - i;
    const dt = new Date(y, m - 1, 1);
    const v = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0');
    sel.appendChild(el('option', { value: v, text: periodLabel(v) }));
  }
  sel.selectedIndex = 1;
  return sel;
}

// ---- Списъкът „Експорти" (раздел в „Склад") ----

export async function renderExportRuns(host, opts) {
  clear(host);
  const kind = opts && opts.kind;
  let runs = [];
  try { runs = await api('/export-runs' + cq() + (kind ? '&kind=' + encodeURIComponent(kind) : '')); } catch (e) { host.appendChild(alertBox('red', e.message)); return; }
  if (!runs.length) {
    host.appendChild(el('p', { class: 'muted', text: kind === 'purchases'
      ? 'Още няма свалени файлове с доставки за Конто от тази фирма. Правят се от „Експорт за Конто" в Доставки.'
      : 'Още няма файлове за Конто от тази фирма. Правят се от „Експорт за Конто" във Фактури, Доставки, Артикули и Склад.' }));
    return;
  }
  if (kind) host.appendChild(el('p', { class: 'muted', text: 'Всеки свален файл се пази такъв, какъвто е излязъл — „Свали същия файл" дава точно него, байт по байт, дори данните в CRM-а после да са се сменили.' }));
  const tb = el('tbody');
  runs.forEach((r) => tb.appendChild(el('tr', {},
    el('td', { text: new Date(r.created_at).toLocaleString('bg-BG', { timeZone: 'Europe/Sofia' }) }),
    el('td', { text: r.kind_label || KIND_LABEL[r.kind] || r.kind }),
    el('td', { text: r.period ? (/^\d{4}-\d{2}$/.test(r.period) ? periodLabel(r.period) : r.period) : '—' }),
    el('td', { class: 'num', text: String((r.counts && r.counts.docs) || 0) }),
    el('td', { class: 'hide-sm', text: (r.warnings || []).length ? plural(r.warnings.length, 'предупреждение', 'предупреждения') : '' }),
    el('td', { class: 'hide-sm', text: r.created_by_name || '' }),
    el('td', {}, el('button', { class: 'btn sm ghost', text: 'Свали същия файл', 'aria-label': 'Свали същия файл ' + (r.filename || ''), onClick: () => downloadRun(r.id, r.filename) }))
  )));
  host.appendChild(el('div', { style: 'overflow:auto' }, el('table', { class: 'export-runs' }, el('thead', {}, el('tr', {},
    el('th', { scope: 'col', text: 'Кога' }), el('th', { scope: 'col', text: 'Вид' }), el('th', { scope: 'col', text: 'Период' }),
    el('th', { scope: 'col', class: 'num', text: 'Бр.' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Бележки' }),
    el('th', { scope: 'col', class: 'hide-sm', text: 'От кого' }), el('th', { scope: 'col', text: 'Файл' }))), tb)));
}

